/**
 * WeaveIn.ai Platform - Form Templates Hook
 * React hook for form template management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  formsAPI,
  fetchAllPages,
  FormTemplate,
  FormTemplateCreate,
  FormTemplateUpdate,
  FormType,
} from '@/services/api/forms';

interface UseFormTemplatesOptions {
  program_id?: number;
  form_type?: FormType;
  is_active?: boolean;
  /** Admin surfaces pass true to see templates that are not released yet. */
  include_drafts?: boolean;
  autoFetch?: boolean;
  /** Rows per request (backend caps at 100; default 50). */
  page_size?: number;
  /** Walk every page instead of stopping at the first (the silent 50-row cap). */
  fetchAll?: boolean;
  /**
   * With a program_id: list the templates for THAT program through
   * GET /by-program/{id} (association table + program-less globals, carrying
   * is_required) instead of /templates/?program_id=, which only matches the
   * legacy form_templates.program_id column.
   */
  programScoped?: boolean;
}

interface UseFormTemplatesReturn {
  templates: FormTemplate[];
  loading: boolean;
  error: string | null;
  createTemplate: (data: FormTemplateCreate) => Promise<FormTemplate | null>;
  updateTemplate: (
    id: number,
    data: FormTemplateUpdate,
    createNewVersion?: boolean,
    enableFieldSync?: boolean
  ) => Promise<FormTemplate | null>;
  deleteTemplate: (id: number) => Promise<boolean>;
  getTemplate: (id: number, version?: number) => Promise<FormTemplate | null>;
  getVersions: (id: number) => Promise<FormTemplate[]>;
  refetch: () => Promise<void>;
}

export default function useFormTemplates(
  options: UseFormTemplatesOptions = {}
): UseFormTemplatesReturn {
  const {
    program_id,
    form_type,
    is_active,
    include_drafts,
    autoFetch = true,
    page_size,
    fetchAll = false,
    programScoped = false,
  } = options;

  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const listParams = { form_type, is_active, include_drafts };
      let data: FormTemplate[];
      if (programScoped && program_id) {
        // /by-program does not return target_entity, and the member surfaces
        // filter on it (a registered_user or activity_event form cannot be
        // filled against a member). Borrow it from the org-wide list, by id and
        // then by name, since that list collapses versions to one row per name.
        const [scoped, orgWide] = await Promise.all([
          formsAPI.getFormsByProgram(program_id),
          fetchAllPages((page, size) =>
            formsAPI.listFormTemplates({ is_active: true, page, page_size: size }),
          ).catch(() => [] as FormTemplate[]),
        ]);
        const byId = new Map(orgWide.map((t) => [t.id, t]));
        const byName = new Map(orgWide.map((t) => [t.name, t]));
        data = (Array.isArray(scoped) ? scoped : []).map((t) => {
          const known = byId.get(t.id) ?? byName.get(t.name);
          return t.target_entity || !known?.target_entity
            ? t
            : { ...t, target_entity: known.target_entity };
        });
        if (form_type) data = data.filter((t) => t.form_type === form_type);
      } else if (fetchAll) {
        data = await fetchAllPages((page, size) =>
          formsAPI.listFormTemplates({ ...listParams, program_id, page, page_size: size }),
        );
      } else {
        data = await formsAPI.listFormTemplates({ ...listParams, program_id, page_size });
      }
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch form templates';
      setError(errorMessage);
      console.error('Error fetching form templates:', err);
    } finally {
      setLoading(false);
    }
  }, [program_id, form_type, is_active, include_drafts, page_size, fetchAll, programScoped]);

  useEffect(() => {
    if (autoFetch) {
      fetchTemplates();
    }
  }, [autoFetch, fetchTemplates]);

  const createTemplate = async (data: FormTemplateCreate): Promise<FormTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const newTemplate = await formsAPI.createFormTemplate(data);
      setTemplates((prev) => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create form template';
      setError(errorMessage);
      console.error('Error creating form template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async (
    id: number,
    data: FormTemplateUpdate,
    createNewVersion: boolean = false,
    enableFieldSync: boolean = false
  ): Promise<FormTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const updatedTemplate = await formsAPI.updateFormTemplate(id, data, createNewVersion, enableFieldSync);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updatedTemplate : t)));
      return updatedTemplate;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update form template';
      setError(errorMessage);
      console.error('Error updating form template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await formsAPI.deleteFormTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete form template';
      setError(errorMessage);
      console.error('Error deleting form template:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getTemplate = async (id: number, version?: number): Promise<FormTemplate | null> => {
    setLoading(true);
    setError(null);

    try {
      const template = await formsAPI.getFormTemplate(id, version);
      return template;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch form template';
      setError(errorMessage);
      console.error('Error fetching form template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getVersions = async (id: number): Promise<FormTemplate[]> => {
    try {
      const versions = await formsAPI.getFormTemplateVersions(id);
      return versions;
    } catch (err: any) {
      console.error('Error fetching template versions:', err);
      return [];
    }
  };

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    getVersions,
    refetch: fetchTemplates,
  };
}
